import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { getCurrentUsername } from 'src/Common/context/AuthContext';
import { useSoccerMatches } from '../../api/useSoccerMatches';
import {
  LSPORTS_TRADE360_PROVIDER,
  MatchExternalRef,
  MatchExternalRefCandidate,
  useConfirmMatchExternalRef,
  useMatchExternalRefCandidates,
  useSetMatchExternalRefActing,
  useSuggestMatchExternalRef
} from '../../api/useMatchExternalRefs';
const NO_OPERATOR_ERROR =
  'Cannot identify the operator — log in again before doing this.';

interface RowState {
  candidates: MatchExternalRefCandidate[] | null;
  picked: MatchExternalRefCandidate | null;
  confirmedRef: MatchExternalRef | null;
  error: string | null;
}

const EMPTY_ROW_STATE: RowState = {
  candidates: null,
  picked: null,
  confirmedRef: null,
  error: null
};

/** One match's fixture-picker row — search → pick → suggest → confirm → acting. */
function Trade360MatchRow({
  groupId,
  matchKey,
  matchName
}: {
  groupId: string;
  matchKey: string;
  matchName: string | null;
}) {
  const [state, setState] = useState<RowState>(EMPTY_ROW_STATE);
  const candidatesMut = useMatchExternalRefCandidates();
  const suggestMut = useSuggestMatchExternalRef();
  const confirmMut = useConfirmMatchExternalRef();
  const actingMut = useSetMatchExternalRefActing();

  const search = () => {
    setState(s => ({ ...s, error: null, candidates: null, picked: null }));
    candidatesMut.mutate(
      { groupId, matchKey },
      {
        onSuccess: candidates =>
          setState(s => ({
            ...s,
            candidates,
            picked: candidates.length === 1 ? candidates[0] : null
          })),
        onError: () =>
          setState(s => ({
            ...s,
            error: 'Failed to search Trade360 fixtures.'
          }))
      }
    );
  };

  const confirm = async (candidate: MatchExternalRefCandidate) => {
    const boundBy = getCurrentUsername();
    if (!boundBy) {
      setState(s => ({ ...s, error: NO_OPERATOR_ERROR }));
      return;
    }
    setState(s => ({ ...s, error: null }));
    try {
      const suggestion = await suggestMut.mutateAsync({
        groupId,
        matchKey,
        provider: LSPORTS_TRADE360_PROVIDER,
        externalId: candidate.fixtureId
      });
      const competitorByPosition = suggestion.competitors.reduce<
        Record<'1' | '2', string>
      >(
        (acc, c) => {
          acc[c.position] = c.providerName;
          return acc;
        },
        { '1': '', '2': '' }
      );
      const ref = await confirmMut.mutateAsync({
        groupId,
        matchKey,
        provider: LSPORTS_TRADE360_PROVIDER,
        externalId: candidate.fixtureId,
        competitorByPosition,
        boundBy
      });
      setState(s => ({ ...s, confirmedRef: ref }));
    } catch {
      setState(s => ({
        ...s,
        error: 'Failed to confirm the Trade360 fixture.'
      }));
    }
  };

  const toggleActing = (checked: boolean) => {
    if (!state.confirmedRef) return;
    const updatedBy = getCurrentUsername();
    if (!updatedBy) {
      setState(s => ({ ...s, error: NO_OPERATOR_ERROR }));
      return;
    }
    actingMut.mutate(
      {
        provider: state.confirmedRef.provider,
        externalId: state.confirmedRef.externalId,
        actingEnabled: checked,
        updatedBy
      },
      {
        onSuccess: ref => setState(s => ({ ...s, confirmedRef: ref, error: null })),
        onError: () =>
          setState(s => ({ ...s, error: 'Failed to change "Activar en vivo".' }))
      }
    );
  };

  const searching = candidatesMut.isLoading;
  const confirming = suggestMut.isLoading || confirmMut.isLoading;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
        {matchName ?? matchKey}
      </Typography>

      {state.error && (
        <Alert severity="warning" sx={{ my: 0.5, py: 0, fontSize: '0.7rem' }}>
          {state.error}
        </Alert>
      )}

      {!state.confirmedRef && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={search}
            disabled={searching}
            sx={{ alignSelf: 'flex-start' }}
          >
            {searching ? (
              <CircularProgress size={14} />
            ) : (
              'Buscar fixture Trade360'
            )}
          </Button>

          {state.candidates != null && state.candidates.length === 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.500' }}>
              sin candidato
            </Typography>
          )}

          {state.candidates != null && state.candidates.length > 0 && (
            <>
              <Stack spacing={0.5}>
                {state.candidates.map(c => {
                  const selected = state.picked?.fixtureId === c.fixtureId;
                  return (
                    <Box
                      key={c.fixtureId}
                      onClick={() => setState(s => ({ ...s, picked: c }))}
                      role="button"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: 1,
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'action.selected' : 'transparent'
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem' }}>
                        {c.participants.join(' vs ')}
                      </Typography>
                      {c.league && (
                        <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
                          {c.league}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
              <Button
                size="small"
                variant="contained"
                disabled={!state.picked || confirming}
                onClick={() => state.picked && confirm(state.picked)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {confirming ? <CircularProgress size={14} /> : 'Confirmar fixture'}
              </Button>
            </>
          )}
        </Stack>
      )}

      {state.confirmedRef && (
        <Box sx={{ mt: 1 }}>
          <Chip
            size="small"
            color="success"
            label={`Trade360 fixture ${state.confirmedRef.externalId}`}
            sx={{ mr: 1, mb: 0.5 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={!!state.confirmedRef.actingEnabled}
                disabled={actingMut.isLoading}
                onChange={e => toggleActing(e.target.checked)}
                inputProps={{ 'aria-label': `Activar en vivo — ${matchKey}` }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.75rem' }}>Activar en vivo</Typography>
            }
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * Trade360 fixture-binding section — one row per match on the group (usually
 * one, for the single-match groups this feature targets). Purely additive to
 * the group's config: binding/acting has NO boot-time coupling on the
 * backend, so — unlike `LiveSignalsSection` right below it — nothing here is
 * gated by `podsDesired`.
 */
export function Trade360FixtureSection({ groupId }: { groupId: string }) {
  const { data } = useSoccerMatches(groupId, true);
  const matches = data?.matches ?? [];

  return (
    <Box>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 1 }}>
        Trade360 fixture binding
      </Typography>
      {matches.length === 0 ? (
        <Typography sx={{ fontSize: '0.72rem', color: 'grey.500' }}>
          No matches on this group yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {matches.map(m => (
            <Trade360MatchRow
              key={m.matchKey}
              groupId={groupId}
              matchKey={m.matchKey}
              matchName={m.matchName}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default Trade360FixtureSection;
